import { Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideMinus, lucidePlus } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';

let nextId = 0;

@Component({
  selector: 'app-number-stepper-input',
  imports: [HlmInputGroupImports, HlmIconImports, HlmButtonImports, HlmFieldImports],
  templateUrl: './number-stepper-input.component.html',
  providers: [
    provideIcons({ lucidePlus, lucideMinus }),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberStepperInputComponent),
      multi: true,
    },
  ],
})
export class NumberStepperInputComponent implements ControlValueAccessor {
  readonly min = input<number | null>(null);
  readonly max = input<number | null>(null);
  readonly step = input<number>(1);
  readonly placeholder = input<string>('0');
  readonly label = input.required<string>();
  readonly invalid = input<boolean>(false);
  readonly errorMessage = input<string>('');

  public readonly stepperId = input<string>(`stepper-${++nextId}`);

  readonly value = signal<number | null>(null);
  readonly disabled = input.required();

  #onChange: (value: number | null) => void = () => {};
  #onTouched: () => void = () => {};

  readonly canDecrement = computed(() => {
    const v = this.value();
    const min = this.min();
    return !this.disabled() && (v === null || min === null || v > min);
  });

  readonly canIncrement = computed(() => {
    const v = this.value();
    const max = this.max();
    return !this.disabled() && (v === null || max === null || v < max);
  });

  writeValue(value: number | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.#onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.#onTouched = fn;
  }

  // setDisabledState(isDisabled: boolean): void {
  //   this.disabled.set(isDisabled);
  // }

  decrement(): void {
    if (!this.canDecrement()) return;
    this.#setValue((this.value() ?? 0) - this.step());
  }

  increment(): void {
    if (!this.canIncrement()) return;
    this.#setValue((this.value() ?? 0) + this.step());
  }

  onInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    const parsed = raw === '' ? null : Number(raw);

    if (parsed !== null && Number.isNaN(parsed)) return;

    this.#setValue(parsed);
  }

  onBlur(): void {
    this.#onTouched();
  }

  #setValue(value: number | null): void {
    const normalized = value === null ? null : this.#clamp(value);
    this.value.set(normalized);
    this.#onChange(normalized);
    this.#onTouched();
  }

  #clamp(value: number): number {
    const min = this.min();
    const max = this.max();

    if (min !== null && value < min) return min;
    if (max !== null && value > max) return max;
    return value;
  }
}
